/**
 * Sidebar — left navigation rail.
 *
 * Two regions, separated by a horizontal border:
 *   - TOP   (scrollable) — section nav buttons from the NAV array
 *   - BOTTOM (fixed)     — tools panel toggle + theme picker
 *
 * The sidebar is purely presentational — it calls back to the parent for
 * section selection and tools toggling. No store access here.
 */
import ThemePicker from '../shared/ThemePicker'
import WalkieTalkieIcon from './WalkieTalkieIcon'

/* Inline SVG for the "Others" nav item — a 3-slot equipment rack icon */
function OthersIcon({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </svg>
  )
}

export interface NavItem {
  id: string
  label: string
  icon: string
  /** If true, render the WalkieTalkieIcon SVG instead of the emoji `icon`. */
  img: boolean
  /** If true, render the OthersIcon SVG instead of the emoji `icon`. */
  othersIcon?: boolean
}

/** Default desktop nav items, in display order. */
export const DEFAULT_NAV: NavItem[] = [
  { id: 'top',                label: 'Home',              icon: '🏠',  img: false },
  { id: 'sec-personnel',      label: 'Personnel on Duty', icon: '👮',  img: false },
  { id: 'sec-officer-badges', label: 'Officer Badges',    icon: '🛡️', img: false },
  { id: 'sec-security-keys',  label: 'Security Keys',     icon: '🔑',  img: false },
  { id: 'sec-sup-radios',     label: 'Radios',            icon: '',    img: true  },
  { id: 'sec-recorders',      label: 'Recorders',         icon: '🎙️', img: false },
  { id: 'sec-others',         label: 'Others',            icon: '',    img: false, othersIcon: true },
  { id: 'sec-notes',          label: 'Notes',             icon: '📝',  img: false },
  { id: 'sec-command',         label: 'Command Dashboard', icon: '🎯',  img: false },
  { id: 'sec-cad',             label: 'Gatehouse',         icon: '📟',  img: false },
  { id: 'sec-activity-map',    label: 'Activity Map',      icon: '🗺️', img: false },
  { id: 'sec-zone-editor',     label: 'Zone Editor',       icon: '✏️', img: false },
]

interface SidebarProps {
  /** Header height — used to offset the fixed sidebar from the top. */
  headerHeight: number
  /** Currently active section id. */
  activeSection: string
  /** Whether the right tools panel is open. */
  toolsOpen: boolean
  /** Optional override for the nav items. Defaults to DEFAULT_NAV. */
  nav?: NavItem[]
  onSelect: (id: string) => void
  onToggleTools: () => void
}

export default function Sidebar({
  headerHeight,
  activeSection,
  toolsOpen,
  nav = DEFAULT_NAV,
  onSelect,
  onToggleTools,
}: SidebarProps) {
  return (
    <div
      style={{
        position: 'fixed',
        top: headerHeight,
        left: 0,
        bottom: 0,
        width: 52,
        background: 'var(--bg-surface)',
        borderRight: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '8px 0 0',
        overflow: 'hidden',
        zIndex: 900,
      }}
    >
      {/* Scrollable nav icons */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          paddingBottom: 4,
        }}
      >
        {nav.map(n => (
          <button
            key={n.id}
            onClick={() => onSelect(n.id)}
            title={n.label}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 18,
              border: 'none',
              background: activeSection === n.id ? 'var(--bg-elevated)' : 'transparent',
              boxShadow: activeSection === n.id ? 'inset 2px 0 0 var(--accent)' : 'none',
              flexShrink: 0,
            }}
          >
            {n.img ? <WalkieTalkieIcon size={22} /> : n.othersIcon ? <OthersIcon size={22} /> : n.icon}
          </button>
        ))}
      </div>

      {/* Tools toggle + theme picker */}
      <div
        style={{
          flexShrink: 0,
          borderTop: '1px solid var(--border)',
          paddingTop: 6,
          paddingBottom: 6,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <button
          onClick={onToggleTools}
          title={toolsOpen ? 'Close tools' : 'Open tools'}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontSize: 18,
            border: 'none',
            background: toolsOpen ? 'var(--bg-elevated)' : 'transparent',
            boxShadow: toolsOpen ? '0 0 0 2px var(--accent)' : 'none',
            transition: 'box-shadow 0.2s, background 0.2s',
            flexShrink: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2" strokeLinecap="round">
            {toolsOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="7" x2="21" y2="7" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="17" x2="21" y2="17" />
              </>
            )}
          </svg>
        </button>
        <ThemePicker />
      </div>
    </div>
  )
}
